import { useCallback, useState } from 'react';
import { faPlus, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { useClientContext } from 'client/context';
import { selectAdapters } from 'ducks/adapters/selectors';
import { setAdapters } from 'ducks/adapters/slices';
import { selectCandleValidations } from 'ducks/candle-validations/selectors';
import { updateOverlay } from 'ducks/overlays/slices';
import { selectPairs } from 'ducks/pairs/selectors';
import { setPairs } from 'ducks/pairs/slices';
import { updateSettings } from 'ducks/settings/slices';
import { selectValidationStatus } from 'ducks/statuses/selectors';
import { setStatuses } from 'ducks/statuses/slices';
import { Button } from 'framework/button';
import { Grid } from 'framework/Grid';
import { GridFieldCell } from 'framework/Grid/cells/GridCell';
import { GridColumn } from 'framework/Grid/columns/GridColumn';
import { Input } from 'framework/Input';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { filterGrid } from 'utils/strings';

import { GridDaysCell } from 'components/cells/GridDaysCell';
import { GridHoursCell } from 'components/cells/GridHoursCell';
import { GridMinutesCell } from 'components/cells/GridMinutesCell';
import { GridPairJobCell } from 'components/cells/GridPairJobCell';
import { GridStatusChipCell } from 'components/cells/GridStatusChipCell';
import { GridAutoSizer } from 'components/grid/GridScrollable';
import { AccordionCheckboxes } from 'components/overlays/accordions/accordion-checkboxes/AccordionCheckboxes';
import { Filters } from 'components/overlays/filters/Filters';
import { ValidationsOverlay } from 'components/overlays/validations/ValidationsOverlay';

import cn from '../BacktestsPage.module.scss';

export const BacktestsGrid = () => {
  const dispatch = useAppDispatch();
  const voladeum_pairs = useAppSelector(selectPairs);
  const voladeum_adapters = useAppSelector(selectAdapters);
  const voladeum_validations = useAppSelector(selectCandleValidations);
  const voladeum_validation_status = useAppSelector(selectValidationStatus);
  const { Voladeum, handleError } = useClientContext();

  const [filter, setFilter] = useState('');

  const onClick = useCallback(async () => {
    try {
      dispatch(updateSettings({ loading: true }));
      const [voladeum_pairs, voladeum_status, voladeum_adapters] = await Promise.all([
        Voladeum.pairs.list(),
        Voladeum.statuses.list(),
        Voladeum.adapters.list(),
      ]);

      dispatch(setPairs(voladeum_pairs.data));
      dispatch(setStatuses(voladeum_status.data));
      dispatch(setAdapters(voladeum_adapters.data));
      dispatch(updateOverlay({ key: 'candle_validations', data: { is_open: true } }));
      dispatch(updateSettings({ loading: false }));
    } catch (err) {
      handleError(err);
    }
  }, [Voladeum, handleError, dispatch]);

  return (
    <div className={cn.grid}>
      <div className={cn.toolbar}>
        <h2>Backtesting</h2>
        <div className={cn.flex} />
        <Input
          left_icon={faSearch}
          value={filter}
          onChange={(input) => setFilter(input.value)}
          width="300px"
        />
        <Filters onClick={onClick}>
          <ValidationsOverlay>
            <AccordionCheckboxes
              label="Adapters"
              options={voladeum_adapters}
              filter_key="candle_validation_adapter_ids"
              value_key="adapter_name"
              id_key="adapter_id"
            />
            <AccordionCheckboxes
              label="Pairs"
              options={voladeum_pairs}
              filter_key="candle_validation_pair_ids"
              value_key="pair_name"
              id_key="pair_id"
            />
            <AccordionCheckboxes
              label="Status"
              options={voladeum_validation_status}
              filter_key="candle_validation_status_ids"
              value_key="status_name"
              id_key="status_id"
            />
          </ValidationsOverlay>
        </Filters>
        <Button left_icon={faPlus}>Create Backtest</Button>
      </div>
      <GridAutoSizer>
        <Grid rows={filterGrid(voladeum_validations, filter)}>
          <GridColumn
            label="Pair"
            sort_field="pair_name"
            sort_direction="desc"
            width={175}
            text_align="left"
          >
            <GridPairJobCell />
          </GridColumn>
          <GridColumn label="Status" sort_field="status_name" width={150}>
            <GridStatusChipCell />
          </GridColumn>
          <GridColumn label="Adapter" sort_field="adapter_name" width={100} grow={1}>
            <GridFieldCell field="adapter_name" />
          </GridColumn>
          <GridColumn label="Minutes" sort_field="minute_count" width={125} grow={1}>
            <GridMinutesCell />
          </GridColumn>
          <GridColumn label="Hours" sort_field="hour_count" width={125} grow={1}>
            <GridHoursCell />
          </GridColumn>
          <GridColumn label="Days" sort_field="day_count" width={125} grow={1}>
            <GridDaysCell />
          </GridColumn>
          <GridColumn label="Since Date" sort_field="since_date" width={150} grow={1}>
            <GridFieldCell field="since_date" />
          </GridColumn>
          <GridColumn label="To Date" sort_field="to_date" width={150} grow={1}>
            <GridFieldCell field="to_date" />
          </GridColumn>
        </Grid>
      </GridAutoSizer>
    </div>
  );
};
